import { intl } from '@beelday/common'
import styled from '@emotion/styled'
import { QuizOption, QuizOutcome } from '../model/quiz'
import {
	Action,
	CenteredHint,
	CtaBar,
	Eyebrow,
	Feedback,
	Option,
	Options,
	Progress,
	Question,
	QuestionImage,
	Screen,
	SendError,
} from './primitives'

/**
 * "The next question will appear automatically", in the action's place: the
 * bar keeps it clear of the floating Boardlet button and solid behind it.
 */
const WaitHint = styled(CenteredHint)`
	flex: 1;
	min-height: 48px;
	margin: 0;
	display: flex;
	align-items: center;
	justify-content: center;
`

type Props = {
	number: number
	total: number
	question: string
	imageUrl?: string
	options: QuizOption[]
	outcome: QuizOutcome
	/** Picked on this phone, not yet confirmed. */
	picked?: string
	sending: boolean
	failed: boolean
	onPick: (id: string) => void
	onConfirm: () => void
}

/**
 * One quiz question, laid out like every other phone question: eyebrow,
 * progress, the question, options, and the action pinned at the bottom. The
 * quiz is a game, so the reveal is shown here as well as on the room's
 * screen: the verdict above the question, the right answer and a wrong pick
 * marked on the options.
 */
export const QuizView = ({
	number,
	total,
	question,
	imageUrl,
	options,
	outcome,
	picked,
	sending,
	failed,
	onPick,
	onConfirm,
}: Props): JSX.Element => {
	const translate = intl.useTranslate()
	const answering = outcome === 'answering'

	return (
		<Screen $withCta data-testid="in_person.mobile.quiz">
			<Eyebrow>
				<intl.Translate>in_person.mobile.quiz</intl.Translate>
			</Eyebrow>
			<Progress
				label={
					<intl.Translate current={number} total={total}>
						in_person.mobile.knowledge_check.progress
					</intl.Translate>
				}
				percent={((number - 1) / total) * 100}
			/>
			{outcome === 'correct' || outcome === 'wrong' ? (
				<Feedback $tone={outcome === 'correct' ? 'good' : 'bad'} role="status">
					<intl.Translate>
						{outcome === 'correct' ? 'quiz.hooray' : 'quiz.oops'}
					</intl.Translate>
				</Feedback>
			) : null}
			<Question>{question}</Question>
			{imageUrl ? <QuestionImage src={imageUrl} alt="" /> : null}
			<Options role="radiogroup">
				{options.map(option => (
					<Option
						key={option.id}
						selected={answering ? picked === option.id : option.mine}
						disabled={!answering || sending}
						result={option.result}
						resultLabel={
							option.result === 'correct'
								? translate('in_person.mobile.quiz.correct_answer')
								: option.result === 'incorrect'
								? translate('in_person.mobile.quiz.wrong_answer')
								: undefined
						}
						onClick={() => onPick(option.id)}
					>
						{option.text}
					</Option>
				))}
			</Options>
			{failed ? <SendError /> : null}
			<CtaBar>
				{answering ? (
					<Action busy={sending} disabled={!picked} onClick={onConfirm}>
						<intl.Translate>quiz.confirm_answer</intl.Translate>
					</Action>
				) : (
					<WaitHint role="status">
						<intl.Translate>quiz.wait_message</intl.Translate>
					</WaitHint>
				)}
			</CtaBar>
		</Screen>
	)
}

export default QuizView
